import React, { useEffect, useState } from "react";
import cn from "classnames";
import { Card, CardDescription, CardTitle } from "campaign-builder/ui";
import { updateSelfServeCampaignData } from "graphQL/mutations/selfServeCampaignData";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { RadioDot, RequiredLabel, FieldError } from "./fields";
import { ReturnAddressSection } from "./ReturnAddressSection";
import { useSaveStatus, trackSave } from "../saveStatus";
import { useTargetingValidation } from "../targetingValidation";

interface MovedOptionProps {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  recommended?: boolean;
  dataCy: string;
}

const MovedOption = ({ selected, onSelect, title, description, recommended, dataCy }: MovedOptionProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    data-cy={dataCy}
    className={cn(
      "flex w-full items-start gap-2 rounded-lg border-solid p-4 text-left",
      selected ? "border-[1.5px] border-[#222336] dark:border-slate-300" : "border border-border"
    )}
  >
    <RadioDot selected={selected} className="mt-0.5" />
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-foreground text-sm font-medium leading-5">{title}</span>
        {recommended && (
          <span className="rounded-full bg-success px-2 py-0.5 text-xs font-medium text-success-foreground">
            Recommended
          </span>
        )}
      </div>
      <span className="text-muted-foreground text-sm leading-5">{description}</span>
    </div>
  </button>
);

export const DeliveryRulesCard = () => {
  const { campaignData, campaignId, updateCampaignState } = useCampaignData();
  const save = useSaveStatus();
  const updateAddressOnMove = campaignData?.updateAddressOnMove;
  const { version: validationVersion } = useTargetingValidation();
  const [validationRequested, setValidationRequested] = useState(false);

  useEffect(() => {
    if (validationVersion > 0) setValidationRequested(true);
  }, [validationVersion]);

  const deliveryRuleError =
    validationRequested && typeof updateAddressOnMove !== "boolean" ? "Select an option to continue." : null;

  const setMovePreference = (value: boolean) => {
    if (!campaignData) return;
    updateCampaignState({ ...campaignData, updateAddressOnMove: value });
    if (campaignId) {
      void trackSave(save, () => updateSelfServeCampaignData({ campaignId, updateAddressOnMove: value })).catch(
        () => undefined
      );
    }
  };

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="delivery">
      <div className="flex flex-col gap-1">
        <CardTitle>Delivery rules</CardTitle>
        <CardDescription>Determine the procedure for when specific cases happens.</CardDescription>
      </div>

      <div className="flex flex-col gap-2">
        <RequiredLabel id="admDeliveryRuleMovedLabel">1. If a recipient has moved</RequiredLabel>
        <FieldError message={deliveryRuleError} id="admDeliveryRuleMovedError" />
        <div
          role="radiogroup"
          aria-labelledby="admDeliveryRuleMovedLabel"
          aria-describedby={deliveryRuleError ? "admDeliveryRuleMovedError" : undefined}
          className="flex flex-col gap-2"
        >
          <MovedOption
            selected={updateAddressOnMove === true}
            onSelect={() => setMovePreference(true)}
            title="Forward to new address"
            description="Ensures delivery to the recipient’s most current address, regardless of distance."
            recommended
            dataCy="admDeliveryRuleMoved-forward"
          />
          <MovedOption
            selected={updateAddressOnMove === false}
            onSelect={() => setMovePreference(false)}
            title="Don't forward"
            description="Ideal for location-specific offers that aren't relevant to those who’ve moved"
            dataCy="admDeliveryRuleMoved-dontForward"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <RequiredLabel>2. If a mail piece cannot be delivered</RequiredLabel>
        <p className="text-muted-foreground text-[13px] leading-[18px]">
          Required by USPS to receive undeliverable pieces. Also helps recipients identify the sender.
        </p>
      </div>

      <ReturnAddressSection />
    </Card>
  );
};
